import { useEffect, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { useOperator } from "@/context/OperatorContext";
import api from "@/lib/api";
import { AuthImage } from "@/components/FileUpload";
import VehicleHistoryPanel from "@/components/VehicleHistoryPanel";
import DayRoutePanel from "@/components/DayRoutePanel";
import { ArrowLeft, Loader2, BatteryCharging, KeyRound, Repeat } from "lucide-react";

const dt = (iso) => (iso ? new Date(iso).toLocaleString() : "—");

function ConditionItem({ icon: Icon, label, number, fileId }) {
  return (
    <div className="rounded-tenant-button border border-tenant-border bg-tenant-bg p-3">
      <p className="flex items-center gap-1.5 text-xs font-medium text-muted-foreground"><Icon className="h-3.5 w-3.5" /> {label}</p>
      <p className="mt-1 font-semibold">{number || "—"}</p>
      {fileId && <AuthImage fileId={fileId} className="mt-2 h-24 w-full rounded-tenant-button object-cover" />}
    </div>
  );
}

export default function VehicleHistory() {
  const { id } = useParams();
  const navigate = useNavigate();
  const { ctx } = useOperator();
  const [swaps, setSwaps] = useState([]);
  const v = ctx?.vehicles?.find((x) => x.id === id);

  useEffect(() => {
    api.get(`/operator/vehicles/${id}/swaps`).then(({ data }) => setSwaps(data)).catch(() => {});
  }, [id]);

  if (!ctx) return <div className="flex justify-center py-16"><Loader2 className="h-6 w-6 animate-spin text-muted-foreground" /></div>;

  return (
    <div data-testid="vehicle-history-page" className="space-y-5">
      <button data-testid="vh-back" onClick={() => navigate("/app/fleet")} className="inline-flex items-center gap-1 text-sm text-muted-foreground hover:text-foreground">
        <ArrowLeft className="h-4 w-4" /> Back to fleet
      </button>

      <div className="rounded-tenant-card border border-tenant-border bg-tenant-surface p-5 shadow-tenant-soft">
        <h1 className="text-xl font-semibold tracking-tight">{v?.reg_number || "Vehicle"} <span className="text-base font-normal text-muted-foreground">· {v?.model}</span></h1>
        <p className="mt-1 text-sm text-muted-foreground">Condition tracking, component swaps & driving-pattern history.</p>
        <div className="mt-4 grid grid-cols-2 gap-2.5 sm:grid-cols-4">
          <ConditionItem icon={BatteryCharging} label="Battery #" number={v?.battery_number} fileId={v?.battery_image} />
          <ConditionItem icon={KeyRound} label="Charger #" number={v?.charger_number} fileId={v?.charger_image} />
          {v?.color && <div className="rounded-tenant-button border border-tenant-border bg-tenant-bg p-3"><p className="text-xs font-medium text-muted-foreground">Colour</p><p className="mt-1 font-semibold">{v.color}</p></div>}
          {v?.insurance_expiry && <div className="rounded-tenant-button border border-tenant-border bg-tenant-bg p-3"><p className="text-xs font-medium text-muted-foreground">Insurance expiry</p><p className="mt-1 font-semibold">{v.insurance_expiry}</p></div>}
        </div>
      </div>

      {swaps.length > 0 && (
        <div className="rounded-tenant-card border border-tenant-border bg-tenant-surface p-5 shadow-tenant-soft">
          <p className="mb-3 flex items-center gap-1.5 text-sm font-semibold"><Repeat className="h-4 w-4" /> Component swap log ({swaps.length})</p>
          <div className="space-y-2">
            {swaps.map((sw) => (
              <div key={sw.id} data-testid={`swap-${sw.id}`} className="rounded-tenant-button border border-tenant-border p-3 text-sm">
                <div className="flex flex-wrap items-center justify-between gap-2">
                  <span className="font-medium capitalize">{sw.component} swap: <span className="text-muted-foreground">{sw.old_number || "—"}</span> → {sw.new_number || "—"}</span>
                  <span className="text-xs text-muted-foreground">{dt(sw.at)} · {sw.swapped_by_name || "staff"}</span>
                </div>
                <p className="mt-1 text-muted-foreground">Reason: {sw.reason}</p>
                {(sw.old_image || sw.new_image) && (
                  <div className="mt-2 flex gap-2">
                    {sw.old_image && <div className="text-center"><AuthImage fileId={sw.old_image} className="h-16 w-16 rounded-tenant-button object-cover" /><p className="text-[10px] text-muted-foreground">before</p></div>}
                    {sw.new_image && <div className="text-center"><AuthImage fileId={sw.new_image} className="h-16 w-16 rounded-tenant-button object-cover" /><p className="text-[10px] text-muted-foreground">after</p></div>}
                  </div>
                )}
              </div>
            ))}
          </div>
        </div>
      )}

      <div className="rounded-tenant-card border border-tenant-border bg-tenant-surface p-5 shadow-tenant-soft">
        <DayRoutePanel vehicleId={id} title="Where it went (daily route & activity)" />
      </div>

      <div className="rounded-tenant-card border border-tenant-border bg-tenant-surface p-5 shadow-tenant-soft">
        <VehicleHistoryPanel vehicleId={id} />
      </div>
    </div>
  );
}
